import { useState, useId } from 'react';
import { Icon } from '../../display/Icon';
import { FormLabel } from '../../forms/FormLabel';
import { useOverflow } from '../../../hooks/useOverflow';
import styles from './Field.module.scss';

type RenderInputOptions = {
  ariaDescribedBy?: string;
  id: string;
  isDisabled: boolean;
  isErrored: boolean;
  isRequired: boolean;
};

type RenderLabelProps = {
  htmlFor: string;
  isDisabled?: boolean;
  isErrored?: boolean;
  isRequired?: boolean;
  label: string;
  requiredText?: React.ReactNode;
};

export type Props = {
  /**
   * Optional error message for the field.
   * If it exists, the field will be considered to have an error.
   */
  error?: string;
  /**
   * The number of help text lines to show (defaults to 1).
   * Any extra text will be hidden behind a "more" button.
   */
  helpTextLineClamp?: number;
  /**
   * `id` for the input field. If this isn't supplied then one will be generated
   * to properly link the `label` to the input.
   */
  inputId?: string;
  /**
   * Whether a more compact version of the field should be rendered.
   */
  isCompact?: boolean;
  /**
   * Indicates whether the field is disabled.
   */
  isDisabled?: boolean;
  /**
   * Indicates whether the field is required.
   */
  isRequired?: boolean;
  /**
   * Text for the `label` element for this field.
   */
  label: string;
  /**
   * Function that returns the help text to be rendered beneath the input.
   */
  renderHelpText?: () => React.ReactNode;
  /**
   * Function that returns the rendered input field.
   */
  renderInput: (opts: RenderInputOptions) => React.ReactNode;
  /**
   * Function that returns the label to be rendered.
   */
  renderLabel?: (labelProps: RenderLabelProps) => React.ReactNode;
  /**
   * Optional text to render inside the required label.
   */
  requiredText?: React.ReactNode;
};

type HelpTextProps = {
  children: React.ReactNode;
  helpTextLineClamp: number;
};

/**
 * Component to render help text which can be expanded and collapsed.
 */
const HelpText = ({ children, helpTextLineClamp }: HelpTextProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isOverflowing, setOverflowingNode] = useOverflow();

  // Only render toggle if it's overflowing.
  const toggle = isOverflowing && (
    <button
      aria-hidden="true"
      className={styles.helpTextToggle}
      onClick={() => setIsOpen(!isOpen)}
      type="button"
      data-zds
    >
      {isOpen ? 'Less' : 'More'}
    </button>
  );
  const style = {
    WebkitLineClamp: isOpen ? undefined : helpTextLineClamp,
  };
  return (
    <div className={styles.helpText} data-zds>
      <div
        className={styles.helpTextInner}
        ref={(node) => setOverflowingNode(node as HTMLElement)}
        data-zds
        style={style}
      >
        <div data-zds>{children}</div>
      </div>
      {toggle}
    </div>
  );
};

/**
 * Wraps an `input` field and renders a `label` and optional help text.
 */
export const Field = ({
  label,
  renderInput,
  error = undefined,
  inputId = undefined,
  isCompact = false,
  isDisabled = undefined,
  isRequired = undefined,
  helpTextLineClamp = 1,
  renderHelpText = undefined,
  renderLabel = undefined,
  requiredText = undefined,
}: Props) => {
  const generatedInputId = useId();

  const id = inputId || generatedInputId;

  const describedById = `${id}-description`;

  const helpText = renderHelpText && renderHelpText();

  return (
    <div
      className={styles.field}
      data-zds
      data-disabled={isDisabled ? 'true' : undefined}
    >
      <div data-zds>
        {renderLabel ? (
          renderLabel({
            htmlFor: id,
            isDisabled: isDisabled,
            isErrored: !!error,
            isRequired: isRequired,
            label: label,
            requiredText: requiredText,
          })
        ) : (
          <FormLabel
            alignItems="start"
            htmlFor={id}
            isDisabled={isDisabled}
            isErrored={!!error}
            isRequired={isRequired}
            requiredText={requiredText}
            size={isCompact ? 'compact' : 'small'}
          >
            {error && (
              <Icon
                color="StatusErrorStronger"
                isBlock={true}
                name="formXCircle"
                size={18}
              />
            )}
            <span data-zds>{label}</span>
          </FormLabel>
        )}
      </div>
      <div data-zds>
        {renderInput({
          ariaDescribedBy: helpText || error ? describedById : undefined,
          id,
          isDisabled: !!isDisabled,
          isErrored: !!error,
          isRequired: !!isRequired,
        })}
      </div>
      <div
        aria-live={error ? 'polite' : 'off'}
        className={styles.helpTextWrapper}
        data-compact={isCompact}
        id={describedById}
        data-zds
      >
        {(helpText || error) && (
          <HelpText helpTextLineClamp={helpTextLineClamp}>
            {error && <span className={styles.error}>{error}</span>}
            {error && helpText && ' '}
            {helpText}
          </HelpText>
        )}
      </div>
    </div>
  );
};
